/**
 * NSFW Content Filter System
 * 
 * Gives users control over what content they see.
 * Projects can be flagged as NSFW, and users can set their preferences.
 * 
 * Important: This is about user choice and safety, not censorship.
 * Academic platforms need to handle mature themes appropriately.
 */

import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import {
  Eye, EyeOff, Shield, ShieldAlert, ShieldCheck, AlertTriangle,
  Settings, Sliders, Check, X, Info, Lock
} from 'lucide-react';
import { cn } from "../../utils/cn";

// ============================================================================
// CONTENT RATING LEVELS
// ============================================================================

export const CONTENT_RATINGS = {
  SAFE: {
    id: 'safe',
    label: 'Safe for All',
    description: 'Appropriate for all audiences',
    color: 'emerald',
    icon: ShieldCheck
  },
  TEEN: {
    id: 'teen',
    label: 'Teen (13+)',
    description: 'May contain mild themes',
    color: 'blue',
    icon: Shield
  },
  MATURE: {
    id: 'mature',
    label: 'Mature (18+)',
    description: 'Contains mature themes or content',
    color: 'amber',
    icon: ShieldAlert
  },
  EXPLICIT: {
    id: 'explicit',
    label: 'Explicit (18+)',
    description: 'Contains explicit content',
    color: 'red',
    icon: AlertTriangle
  }
};

// Map rating IDs to numeric levels for comparison
const RATING_LEVELS = {
  safe: 0,
  teen: 1,
  mature: 2,
  explicit: 3
};

// ============================================================================
// NSFW CONTEXT
// ============================================================================

const NSFWContext = createContext(null);

export const NSFWProvider = ({ children }) => {
  // Load user preferences from localStorage
  const [preferences, setPreferences] = useState(() => {
    try {
      const saved = localStorage.getItem('nsfw_preferences');
      return saved ? JSON.parse(saved) : {
        maxRating: 'teen', // Default: show up to teen content
        showWarnings: true, // Show content warnings before revealing
        blurNSFW: true, // Blur NSFW images by default
        ageVerified: false // User has confirmed they're 18+
      };
    } catch {
      return {
        maxRating: 'teen',
        showWarnings: true,
        blurNSFW: true,
        ageVerified: false
      };
    }
  });
  
  // Save preferences when they change
  useEffect(() => {
    localStorage.setItem('nsfw_preferences', JSON.stringify(preferences));
  }, [preferences]);
  
  // Update a single preference
  const updatePreference = useCallback((key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  }, []);
  
  // Check if content should be visible based on rating
  const isContentVisible = useCallback((contentRating = 'safe') => {
    const userLevel = RATING_LEVELS[preferences.maxRating] || 0;
    const contentLevel = RATING_LEVELS[contentRating] || 0;
    return contentLevel <= userLevel;
  }, [preferences.maxRating]);
  
  // Check if content requires age verification
  const requiresAgeVerification = useCallback((contentRating = 'safe') => {
    const contentLevel = RATING_LEVELS[contentRating] || 0;
    return contentLevel >= RATING_LEVELS.mature && !preferences.ageVerified;
  }, [preferences.ageVerified]);
  
  // Set age verification status
  const verifyAge = useCallback((isVerified) => {
    updatePreference('ageVerified', isVerified);
  }, [updatePreference]);
  
  const value = {
    preferences,
    updatePreference,
    isContentVisible,
    requiresAgeVerification,
    verifyAge,
    CONTENT_RATINGS,
    RATING_LEVELS
  };
  
  return (
    <NSFWContext.Provider value={value}>
      {children}
    </NSFWContext.Provider>
  );
};

export const useNSFW = () => {
  const context = useContext(NSFWContext);
  if (!context) {
    throw new Error('useNSFW must be used within NSFWProvider');
  }
  return context;
};

// ============================================================================
// NSFW SETTINGS PANEL
// ============================================================================

export const NSFWSettingsPanel = ({ className }) => {
  const { preferences, updatePreference, verifyAge } = useNSFW();
  const [showAgeModal, setShowAgeModal] = useState(false);
  
  const handleMaxRatingChange = (rating) => {
    // If trying to access mature/explicit content, require age verification
    if (RATING_LEVELS[rating] >= RATING_LEVELS.mature && !preferences.ageVerified) {
      setShowAgeModal(true);
      return;
    }
    updatePreference('maxRating', rating);
  };
  
  const handleAgeVerify = (confirmed) => {
    if (confirmed) {
      verifyAge(true);
      updatePreference('maxRating', 'mature');
    }
    setShowAgeModal(false);
  };
  
  return (
    <div className={cn('space-y-6', className)}>
      <div>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-400" />
          Content Preferences
        </h3>
        <p className="text-sm text-zinc-400">
          Control what type of content you see on the platform.
        </p>
      </div>
      
      {/* Content Rating Slider/Selector */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-zinc-300">
          Maximum Content Rating
        </label>
        
        <div className="grid grid-cols-2 gap-2">
          {Object.values(CONTENT_RATINGS).map((rating) => {
            const Icon = rating.icon;
            const isSelected = preferences.maxRating === rating.id;
            const isLocked = RATING_LEVELS[rating.id] >= RATING_LEVELS.mature && !preferences.ageVerified;
            
            return (
              <button
                key={rating.id}
                onClick={() => handleMaxRatingChange(rating.id)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left',
                  isSelected
                    ? `border-${rating.color}-500 bg-${rating.color}-500/10`
                    : 'border-zinc-700 hover:border-zinc-600',
                  isLocked && 'opacity-75'
                )}
              >
                <div className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  isSelected ? `bg-${rating.color}-500/20` : 'bg-zinc-800'
                )}>
                  {isLocked ? (
                    <Lock className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <Icon className={cn(
                      'w-4 h-4',
                      isSelected ? `text-${rating.color}-400` : 'text-zinc-400'
                    )} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    'font-medium',
                    isSelected ? 'text-white' : 'text-zinc-300'
                  )}>
                    {rating.label}
                    {isLocked && <span className="ml-1 text-xs text-zinc-500">(18+)</span>}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {rating.description}
                  </p>
                </div>
                {isSelected && !isLocked && (
                  <Check className={`w-4 h-4 text-${rating.color}-400 flex-shrink-0`} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Additional Settings */}
      <div className="space-y-3 pt-4 border-t border-zinc-800">
        {/* Show Warnings Toggle */}
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-zinc-300">Show Content Warnings</p>
              <p className="text-xs text-zinc-500">Display a warning before showing sensitive content</p>
            </div>
          </div>
          <Switch
            checked={preferences.showWarnings}
            onCheckedChange={(v) => updatePreference('showWarnings', v)}
          />
        </label>
        
        {/* Blur NSFW Toggle */}
        <label className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center gap-3">
            <EyeOff className="w-4 h-4 text-zinc-400" />
            <div>
              <p className="text-sm font-medium text-zinc-300">Blur Sensitive Images</p>
              <p className="text-xs text-zinc-500">Blur thumbnails until you choose to view them</p>
            </div>
          </div>
          <Switch
            checked={preferences.blurNSFW}
            onCheckedChange={(v) => updatePreference('blurNSFW', v)}
          />
        </label>
      </div>
      
      {/* Info Box */}
      <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-zinc-400">
            <p className="mb-2">
              <strong className="text-zinc-300">Why we have content ratings:</strong>
            </p>
            <p>
              Enume is an academic platform that may include mature themes in art, 
              literature, medical studies, and other disciplines. Content ratings help 
              ensure everyone can use the platform comfortably.
            </p>
          </div>
        </div>
      </div>
      
      {/* Age Verification Modal */}
      {showAgeModal && (
        <AgeVerificationModal
          onConfirm={() => handleAgeVerify(true)}
          onCancel={() => handleAgeVerify(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// SWITCH COMPONENT (inline for this file)
// ============================================================================

const Switch = ({ checked, onCheckedChange, disabled }) => (
  <button
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange?.(!checked)}
    className={cn(
      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
      checked ? 'bg-purple-600' : 'bg-zinc-700',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    <span
      className={cn(
        'inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1'
      )}
    />
  </button>
);

// ============================================================================
// AGE VERIFICATION MODAL
// ============================================================================

const AgeVerificationModal = ({ onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/20 flex items-center justify-center">
            <ShieldAlert className="w-8 h-8 text-amber-400" />
          </div>
          
          <h3 className="text-xl font-bold text-white mb-2">
            Age Verification Required
          </h3>
          
          <p className="text-zinc-400 mb-6">
            You must be 18 years or older to view mature content. 
            By proceeding, you confirm that you are of legal age in your jurisdiction.
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg font-medium hover:bg-zinc-700 transition-colors"
            >
              I'm Under 18
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-500 transition-colors"
            >
              I'm 18 or Older
            </button>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-zinc-800/50 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">
            This verification is stored locally on your device and 
            can be reset in your settings.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CONTENT RATING BADGE (for displaying on projects)
// ============================================================================

export const ContentRatingBadge = ({ rating = 'safe', size = 'sm', className }) => {
  const ratingConfig = CONTENT_RATINGS[rating.toUpperCase()] || CONTENT_RATINGS.SAFE;
  const Icon = ratingConfig.icon;
  
  const sizes = {
    xs: 'px-1.5 py-0.5 text-[10px] gap-1',
    sm: 'px-2 py-1 text-xs gap-1.5',
    md: 'px-2.5 py-1.5 text-sm gap-2'
  };
  
  const colors = {
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    red: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  
  return (
    <span className={cn(
      'inline-flex items-center rounded-full font-medium border',
      sizes[size],
      colors[ratingConfig.color],
      className
    )}>
      <Icon className="w-3 h-3" />
      {ratingConfig.label}
    </span>
  );
};

// ============================================================================
// CONTENT WARNING OVERLAY
// ============================================================================

export const ContentWarningOverlay = ({ 
  rating = 'mature',
  reason,
  onReveal,
  className 
}) => {
  const { preferences } = useNSFW();
  const ratingConfig = CONTENT_RATINGS[rating.toUpperCase()] || CONTENT_RATINGS.MATURE;
  const Icon = ratingConfig.icon;
  
  if (!preferences.showWarnings) {
    return null;
  }
  
  return (
    <div className={cn(
      'absolute inset-0 z-10 flex flex-col items-center justify-center p-4 text-center',
      'bg-zinc-900/95 backdrop-blur-md',
      className
    )}>
      <div className={cn(
        'w-12 h-12 rounded-full flex items-center justify-center mb-3',
        `bg-${ratingConfig.color}-500/20`
      )}>
        <Icon className={`w-6 h-6 text-${ratingConfig.color}-400`} />
      </div>
      
      <h4 className="text-white font-semibold mb-1">
        Sensitive Content
      </h4>
      
      <p className="text-sm text-zinc-400 mb-4 max-w-xs">
        {reason || `This content is rated ${ratingConfig.label.toLowerCase()}.`}
      </p>
      
      <button
        onClick={onReveal}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
          `bg-${ratingConfig.color}-500/20 text-${ratingConfig.color}-400`,
          `hover:bg-${ratingConfig.color}-500/30`
        )}
      >
        <Eye className="w-4 h-4" />
        Show Content
      </button>
    </div>
  );
};

// ============================================================================
// NSFW IMAGE WRAPPER
// ============================================================================

export const NSFWImage = ({ 
  src, 
  alt, 
  rating = 'safe',
  reason,
  className,
  ...props 
}) => {
  const { preferences, isContentVisible, requiresAgeVerification } = useNSFW();
  const [revealed, setRevealed] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(false);
  
  const shouldBlur = preferences.blurNSFW && 
    !revealed && 
    RATING_LEVELS[rating] >= RATING_LEVELS.mature;
  
  const shouldHide = !isContentVisible(rating);
  const needsAgeVerify = requiresAgeVerification(rating);
  
  const handleReveal = () => {
    if (needsAgeVerify) {
      setShowAgeModal(true);
    } else {
      setRevealed(true);
    }
  };
  
  if (shouldHide) {
    return (
      <div className={cn(
        'flex items-center justify-center bg-zinc-800 text-zinc-500',
        className
      )}>
        <div className="text-center p-4">
          <EyeOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Content hidden by your settings</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={cn('relative overflow-hidden', className)}>
      <img
        src={src}
        alt={alt}
        className={cn(
          'w-full h-full object-cover transition-all duration-300',
          shouldBlur && 'blur-xl scale-110'
        )}
        {...props}
      />
      
      {shouldBlur && (
        <ContentWarningOverlay
          rating={rating}
          reason={reason}
          onReveal={handleReveal}
        />
      )}
      
      {showAgeModal && (
        <AgeVerificationModal
          onConfirm={() => {
            setShowAgeModal(false);
            setRevealed(true);
          }}
          onCancel={() => setShowAgeModal(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// CONTENT RATING SELECTOR (for project creation)
// ============================================================================

export const ContentRatingSelector = ({ value, onChange, className }) => {
  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-sm font-medium text-zinc-300">
        Content Rating
      </label>
      <p className="text-xs text-zinc-500 mb-3">
        Select the appropriate rating for your project
      </p>
      
      <div className="grid grid-cols-2 gap-2">
        {Object.values(CONTENT_RATINGS).map((rating) => {
          const Icon = rating.icon;
          const isSelected = value === rating.id;
          
          return (
            <button
              key={rating.id}
              type="button"
              onClick={() => onChange(rating.id)}
              className={cn(
                'flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left',
                isSelected
                  ? `border-${rating.color}-500 bg-${rating.color}-500/10`
                  : 'border-zinc-700 hover:border-zinc-600'
              )}
            >
              <Icon className={cn(
                'w-4 h-4',
                isSelected ? `text-${rating.color}-400` : 'text-zinc-400'
              )} />
              <span className={cn(
                'text-sm font-medium',
                isSelected ? 'text-white' : 'text-zinc-300'
              )}>
                {rating.label}
              </span>
            </button>
          );
        })}
      </div>
      
      {value && RATING_LEVELS[value] >= RATING_LEVELS.mature && (
        <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          This content will be hidden from users under 18
        </p>
      )}
    </div>
  );
};

export default {
  NSFWProvider,
  useNSFW,
  NSFWSettingsPanel,
  ContentRatingBadge,
  ContentWarningOverlay,
  NSFWImage,
  ContentRatingSelector,
  CONTENT_RATINGS,
  RATING_LEVELS
};